import React, { useEffect, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../MUIs';
import BottomSheet from './BottomSheet';
import Canvas from '../Canvas/Canvas';
import Commons from '../../Services/Common/Common';
import { showSnackbar } from '../../Reducer/SnackbarReducer';
import { BottomSheetActions } from '../../Reducer/BottomSheetReducer';
import { PermitActions } from '../../Reducer/PermitReducer';

const HEADER_TYPO = {
  fontSize: '18px',
  fontWeight: 'bold',
  display: 'flex',
  flexWrap: 'wrap',
  lineHeight: 1.4,
};

export function createSignFileFromDataUrl(dataURL) {
  const decodedData = atob(dataURL.split(',')[1]);
  const byteNumbers = new Array(decodedData.length);
  for (let i = 0; i < decodedData.length; i++) {
    byteNumbers[i] = decodedData.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'image/png' });
  return new File(
    [blob],
    `${new Date()
      .toISOString()
      .replace(/T/, '')
      .replace(/\..+/, '')
      .replace(/-/g, '')
      .replace(/:/g, '')}.png`,
    { type: 'image/png' }
  );
}

/**
 * 통합 SignBottomSheet
 *
 * signMode
 * - registration   : TBM/회의 서명등록
 * - permitNew      : PermitNew 승인·요청·연장
 * - witness        : 입회 확인
 * - permitLegacy   : 구 Permit (approverList gubun)
 * - permitAuthCd   : M_PER_BASIC (authCd + SaveLogic1)
 */
export default function SignBottomSheet({
  signMode = 'permitNew',
  signatureCanvas,
  Save1,
  SaveLogic1,
  handleSignRequest,
  onComplete,
  onSave,
  onDismiss,
  onWorkEndStepComplete,
  setMode,
  authorName,
  userInfo: userInfoProp,
  initialImageUrl = '',
  workTypeNm = '',
  isWorkEndFlow = false,
  signInputs: signInputsProp,
  setSignInputs: setSignInputsProp,
  alarmData = [],
  signUpload = null,
  confirmDisabled = false,
}) {
  const dispatch = useDispatch();
  const { baseInfo = {} } = useSelector((state) => state.permit || {});
  const { userInfo: userInfoStore = {} } = useSelector(
    (state) => state.BaseInfo || {}
  );
  const userInfo = userInfoProp || userInfoStore;

  const isRegistration = signMode === 'registration';
  const isControlled = isRegistration && setSignInputsProp;

  const [internalSignInputs, setInternalSignInputs] = useState({
    mapUrl: initialImageUrl,
    mapFiles: {},
  });
  const signInputs = isControlled ? signInputsProp : internalSignInputs;
  const setSignInputs = isControlled ? setSignInputsProp : setInternalSignInputs;

  const [disabled, setDisabled] = useState(false);

  const dailyStatus =
    baseInfo?.dailies?.[0]?.status || baseInfo?.mst?.status || '';

  useEffect(() => {
    if (!isRegistration) return;
    setDisabled(
      alarmData.find((f) => f.id === Commons.DecryptoAES(userInfo?.userid))
        ?.checkYn === 'Y' || !!alarmData.find((f) => f.state === '3')
    );
  }, [alarmData, isRegistration, userInfo?.userid]);

  useEffect(() => {
    if (isRegistration || isControlled) return;
    setSignInputs({
      mapUrl: initialImageUrl || '',
      mapFiles: {},
    });
  }, [initialImageUrl, isRegistration, isControlled]);

  useEffect(() => {
    if (!isWorkEndFlow || isRegistration || isControlled) return;
    signatureCanvas?.current?.clear();
    setSignInputs({ mapUrl: '', mapFiles: {} });
  }, [workTypeNm, isWorkEndFlow, isRegistration, isControlled, signatureCanvas]);

  const renderHeaderText = () => {
    if (signMode === 'witness') {
      return workTypeNm ? (
        <>
          [{workTypeNm}] 작업종류 입회 확인을{' '}
          <span style={{ color: '#57B84D' }}>완료</span>합니다.
        </>
      ) : (
        <>
          입회 확인을 <span style={{ color: '#57B84D' }}>완료</span>합니다.
        </>
      );
    }

    if (signMode === 'permitLegacy') {
      return (
        <>
          {baseInfo?.name}님의 작업허가서를&nbsp;
          <span style={{ color: '#57B84D' }}>승인</span>합니다.
        </>
      );
    }

    if (signMode === 'permitAuthCd') {
      return (
        <>
          {authorName}님의 작업허가서를&nbsp;
          <span style={{ color: '#57B84D' }}>승인</span>합니다.
        </>
      );
    }

    if (['승인요청', '결재요청', '연장요청'].includes(dailyStatus)) {
      return (
        <>
          {baseInfo?.mst?.reqNm}님의 작업허가서를&nbsp;
          <span style={{ color: '#57B84D' }}>승인</span>합니다.
        </>
      );
    }

    return (
      <>
        작업허가서를 <span style={{ color: '#57B84D' }}>&nbsp;요청</span>합니다.
      </>
    );
  };

  const handleCanvasEnd = () => {
    if (!signatureCanvas?.current) return;

    const dataURL = signatureCanvas.current.toDataURL('image/png');
    const file = createSignFileFromDataUrl(dataURL);
    const next = {
      mapUrl: isRegistration
        ? signatureCanvas.current.toDataURL()
        : dataURL,
      mapFiles: file,
    };

    setSignInputs(next);

    if (isRegistration && signUpload) {
      signUpload.clearFiles?.();
      signUpload.FileChange?.({ target: { files: [file] } });
    }
  };

  /** state 반영 전 「완료」 클릭 등 — 캔버스에서 서명 직접 읽기 */
  const resolveSignPayload = () => {
    if (signInputs.mapUrl && signInputs.mapFiles) {
      return signInputs;
    }

    const canvas = signatureCanvas?.current;
    if (!canvas || canvas.isEmpty?.()) {
      return signInputs;
    }

    const dataURL = canvas.toDataURL('image/png');
    return {
      mapUrl: dataURL,
      mapFiles: createSignFileFromDataUrl(dataURL),
    };
  };

  const handleReset = () => {
    signatureCanvas?.current?.clear();
    signUpload?.clearFiles?.();
    setSignInputs({ mapUrl: '', mapFiles: {} });
    if (isRegistration) setDisabled(false);
  };

  const resolvePermitNewGubun = () => {
    if (dailyStatus === '승인요청') return '2';
    if (dailyStatus === '결재요청') return '1';
    return '';
  };

  const resolvePermitLegacyGubun = () => {
    let gubun = '1';
    const isIAmSafetyApprover =
      baseInfo?.safetyApproverId === userInfo?.decUserId;
    const myDepSign = baseInfo?.approverList?.find(
      (a) => String(a.approverId) === String(userInfo?.decUserId)
    );
    const isDepSignDone = myDepSign
      ? myDepSign.signaturePath && myDepSign.signaturePath !== ''
      : true;

    if (isIAmSafetyApprover) {
      gubun = isDepSignDone ? '2' : '1';
    }
    return gubun;
  };

  const setPermitMode = (gubun) => {
    if (signMode === 'permitLegacy') {
      if (gubun === '2') setMode?.('signSm');
      else setMode?.('signDep');
      return;
    }
    if (gubun === '2') dispatch(PermitActions.setMode('signSm'));
    else if (gubun === '1') dispatch(PermitActions.setMode('signDep'));
    else dispatch(PermitActions.setMode('write'));
  };

  const handleComplete = async () => {
    const resolvedSign = resolveSignPayload();

    if (!resolvedSign.mapUrl) {
      dispatch(
        showSnackbar({ message: '서명을 완료해주세요.', type: 'warning' })
      );
      return;
    }

    if (!isControlled && resolvedSign !== signInputs) {
      setSignInputs(resolvedSign);
    }

    if (isRegistration) {
      onSave?.();
      return;
    }

    try {
      if (signMode === 'witness') {
        dispatch(PermitActions.setMode('witness'));
        await Save1?.({
          signFile: resolvedSign.mapFiles,
          signUrl: resolvedSign.mapUrl,
        });

        if (isWorkEndFlow && typeof onWorkEndStepComplete === 'function') {
          const hasMore = onWorkEndStepComplete();
          if (hasMore) {
            signatureCanvas?.current?.clear();
            setSignInputs({ mapUrl: '', mapFiles: {} });
            dispatch(
              showSnackbar({
                message: `[${workTypeNm}] 서명 완료. 다음 작업종류를 서명해 주세요.`,
                type: 'success',
              })
            );
            return;
          }
        }
      } else if (signMode === 'permitAuthCd') {
        const gubun = userInfo?.authCd === 'HEAD_MANAGER' ? '2' : '1';
        if (gubun === '2') {
          setMode?.('signSm');
          await handleSignRequest?.('2');
        } else {
          setMode?.('signDep');
          await handleSignRequest?.('1');
        }
        await SaveLogic1?.(gubun, '', resolvedSign.mapFiles);
      } else if (signMode === 'permitLegacy') {
        const gubun = resolvePermitLegacyGubun();
        setPermitMode(gubun);
        await Save1?.({ gubun, signFile: resolvedSign.mapFiles });
      } else {
        const gubun = resolvePermitNewGubun();
        setPermitMode(gubun);
        await Save1?.({ gubun, signFile: resolvedSign.mapFiles });
      }

      dispatch(BottomSheetActions.CloseBottomSheet());

      if (typeof onComplete === 'function') onComplete();
      else if (typeof onDismiss === 'function') onDismiss();
      else if (signMode === 'permitNew' || signMode === 'witness') {
        dispatch(PermitActions.setMode(''));
      }
    } catch (error) {
      console.error('저장 중 오류 발생:', error);
      dispatch(
        showSnackbar({
          message: '저장 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  const showResetInHeader = isRegistration && alarmData[0]?.state !== '3';

  const buttonLabel = isRegistration ? '확인' : '완료';
  const buttonSx = isRegistration
    ? {
        mb: 2,
        width: '100%',
        height: '36px',
        borderRadius: '18px',
        backgroundColor: '#2DB400',
        '&:hover': { backgroundColor: '#26a300' },
      }
    : {
        width: '100%',
        height: 36,
        borderRadius: '5px',
        backgroundColor: '#57B84D',
        color: '#fff',
        boxShadow: 'none',
      };

  return (
    <BottomSheet
      onDismiss={onDismiss}
      isHeaderChildren
      headerText={isRegistration ? '서명등록' : ''}
      headerTextSx={isRegistration ? { fontWeight: 'bold' } : undefined}
      headerChildren={
        isRegistration ? (
          showResetInHeader && (
            <Chip
              sx={{ borderRadius: '8px', ml: 'auto' }}
              label="재설정"
              size="small"
              variant="outlined"
              onClick={handleReset}
            />
          )
        ) : (
          <Typography sx={HEADER_TYPO}>{renderHeaderText()}</Typography>
        )
      }
    >
      <Stack gap={1} sx={{ px: isRegistration ? 0 : 1 }}>
        {!isRegistration && (
          <Typography sx={{ color: '#918A8A',  fontSize: '14px' }}>
            {signMode === 'witness' && isWorkEndFlow
              ? '작업종료 전 모든 작업종류에 서명이 필요합니다. 서명해주세요.'
              : '서명해주세요.'}
          </Typography>
        )}

        <Box>
          {!isRegistration && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                서명
              </Typography>
              <Chip
                sx={{ borderRadius: '8px' }}
                label="재설정"
                size="small"
                variant="outlined"
                onClick={handleReset}
              />
            </Box>
          )}

          <Box sx={{ borderRadius: '8px',   }}>
            <Canvas
              signatureCanvas={signatureCanvas}
              userId={userInfo?.userNm}
              imageUrl={signInputs.mapUrl}
              disabled={isRegistration ? disabled : false}
              onEnd={handleCanvasEnd}
            />
          </Box>
        </Box>

        {isRegistration && (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
            [필수] 서명 정보 수집 및 이용에 동의합니다.
          </Typography>
        )}

        <Box
          p={isRegistration ? 0 : 1}
          bgcolor="#fff"
          sx={
            !isRegistration
              ? {
                  borderTopLeftRadius: 20,
                  borderTopRightRadius: 20,
                }
              : undefined
          }
        >
          <Button
            name={buttonLabel}
            sx={buttonSx}
            onClick={handleComplete}
            disabled={isRegistration ? disabled || confirmDisabled : false}
          />
        </Box>
      </Stack>
    </BottomSheet>
  );
}
